import kiwApi from '@/api/v1/kiw';
import router from '@/router/index';
import utils from '@/helpers/utils';

const kiw = {
  id: '',
  department: '',
  files: [],
  kiwNumber: '',
  name: '',
  notes: '',
  project: null,
  purpose: '',
  updatedAt: null,
  guardNotes: '',
};

export default {
  namespaced: true,

  state: {
    kiw,
    draft: null,
    actions: [],
    isProcessing: false,
  },

  mutations: {
    SET_SINGLE_KIW(state, kiwData) {
      state.kiw = kiwData;
    },

    RESET_KIW_OBJECT(state) {
      state.kiw = kiw;
    },

    ADD_FILE(state, file) {
      state.kiw.files.push(file);
    },

    REMOVE_FILES(state, fileIds) {
      state.kiw.files = state.kiw.files.filter((file) => !fileIds.includes(file.id));
    },

    CHANGE_PROCESSING_STATUS(state, status) {
      state.isProcessing = status;
    },

    SET_KIW_ACTIONS(state, actions) {
      state.actions = actions;
    },

    SET_DRAFT(state, draft) {
      state.draft = draft;
    },
  },

  actions: {
    fetchSingleKIW({ commit }, id) {
      commit('CHANGE_LOADING_STATUS', true, { root: true });

      return new Promise((resolve) => {
        kiwApi.show(id)
          .then((resp) => {
            commit('SET_SINGLE_KIW', resp.data);
            resolve(resp.data);
          })
          .finally(() => {
            commit('CHANGE_LOADING_STATUS', false, { root: true });
          });
      });
    },

    createSingleKIW({ commit, dispatch, rootState }, kiwData) {
      commit('CHANGE_PROCESSING_STATUS', true);

      const kiwFormData = new FormData();
      utils.buildFormData(kiwFormData, kiwData);

      kiwApi.create(kiwFormData)
        .then((resp) => {
          const successMessage = 'Utworzono wniosek KIW';

          dispatch('snackbar/showSnackbar', {
            message: [successMessage],
            type: 'success',
          }, { root: true });
          commit('CHANGE_PROCESSING_STATUS', false);
          router.push({ name: 'kiw-index', params: { startTab: 'pending' } });
        })
        .catch(() => {
          commit('CHANGE_PROCESSING_STATUS', false);
        });
    },

    deleteSingleKIW({ commit, dispatch }, kiwId) {
      commit('CHANGE_PROCESSING_STATUS', true);

      kiwApi.delete(kiwId)
        .then(() => {
          dispatch('snackbar/showSnackbar', {
            message: ['Usunięto wniosek'],
            type: 'success',
          }, { root: true });
          commit('CHANGE_PROCESSING_STATUS', false);
          router.push({ name: 'kiw-index', params: { startTab: 'pending' } });
        })
        .catch(() => {
          commit('CHANGE_PROCESSING_STATUS', false);
        });
    },

    editSingleKIW({ commit, dispatch, rootState }, kiwData) {
      commit('CHANGE_PROCESSING_STATUS', true);
      const { id } = kiwData;

      kiwApi.update(id, kiwData)
        .then(() => {
          const successMessage = 'Zmiany we wniosku KIW zostały zapisane';

          dispatch('snackbar/showSnackbar', {
            message: [successMessage],
            type: 'success',
          }, { root: true });
          commit('CHANGE_PROCESSING_STATUS', false);
          router.push({ name: 'kiw-single', params: { id } });
        })
        .finally(() => {
          commit('CHANGE_PROCESSING_STATUS', false);
        });
    },

    uploadKIWFile({ commit, dispatch, state }, fileFormData) {
      return new Promise((resolve, reject) => {
        kiwApi.files.upload(state.kiw.id, fileFormData)
          .then((resp) => {
            dispatch('snackbar/showSnackbar', {
              message: ['Plik został dodany'],
              type: 'success',
            }, { root: true });
            commit('ADD_FILE', resp.data);
            resolve();
          })
          .catch(() => {
            reject();
          });
      });
    },

    downloadKIWFiles({ commit }, fileIds) {
      commit('CHANGE_PROCESSING_STATUS', true);

      return kiwApi.files.downloadFiles(fileIds)
        .then((resp) => {
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/zip' }));
          link.setAttribute('download', `Pliki KIW [${fileIds.length}]`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        .finally(() => {
          commit('CHANGE_PROCESSING_STATUS', false);
        });
    },

    destroyKIWFiles({ commit, dispatch, state }, fileIds) {
      commit('CHANGE_PROCESSING_STATUS', true);

      return new Promise((resolve) => {
        kiwApi.files.delete(state.kiw.id, fileIds)
          .then(() => {
            dispatch('snackbar/showSnackbar', {
              message: ['Plik(i) zostały usunięte'],
              type: 'success',
            }, { root: true });
            commit('REMOVE_FILES', fileIds);
            resolve();
          })
          .finally(() => {
            commit('CHANGE_PROCESSING_STATUS', false);
          });
      });
    },

    fetchKIWActions({ commit }, kiwId) {
      return new Promise((resolve) => {
        kiwApi.actions.show(kiwId)
          .then((response) => {
            commit('SET_KIW_ACTIONS', response.data);
            resolve();
          });
      });
    },

    makeKIWAction({ commit, dispatch, state }, actionData) {
      dispatch('changeActionStatus', true, { root: true });

      kiwApi.actions.set(state.kiw.id, actionData)
        .then((response) => {
          dispatch('dialog/clearDialog', null, { root: true });

          dispatch('snackbar/showSnackbar', {
            message: ['Akcja wykonana'],
            type: 'success',
          }, { root: true });

          commit('SET_KIW_ACTIONS', response.data);
          dispatch('fetchSingleKIW', state.kiw.id);
        })
        .finally(() => {
          dispatch('changeActionStatus', false, { root: true });
        });
    },

    saveDraft({ commit }, draft) {
      commit('SET_DRAFT', draft);
    },

    deleteDraft({ commit }) {
      commit('SET_DRAFT', null);
    },

    resetSingleKIW({ commit, dispatch }) {
      commit('RESET_KIW_OBJECT');
      commit('SET_KIW_ACTIONS', []);
      dispatch('changeActionStatus', false, { root: true });
    },

    changeProcessingStatus({ commit }, status) {
      commit('CHANGE_PROCESSING_STATUS', status);
    },
  },
};
